import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@chakra-ui/react';
import { useAtomValue } from 'jotai';
import { fetchApi } from '../../lib/apiClient';
import { pushyManager } from '../../services/pushyManager';
import { pushyDeviceTokenAtom, pushyInitializedAtom } from '../../atoms/v3/pushyAtoms';
import { usePushyManager } from '../../hooks/usePushyManager';
import { logger } from '../../lib/logger';
import { frontendLogger } from '../../atoms/v3/debugAtoms';
import { isPushyPluginAvailable } from '../../services/pushy';

// 双重日志记录：同时发送到 Axiom 和前端调试面板
const dualLog = {
  info: (message: string, data?: any) => {
    logger.info(message, data);
    frontendLogger.info(message, data);
  },
  warn: (message: string, data?: any) => {
    logger.warn(message, data);
    frontendLogger.warn(message, data);
  },
  error: (message: string, data?: any) => {
    logger.error(message, data);
    frontendLogger.error(message, data);
  }
};

export const PushyTestPanel: React.FC = () => {
  const { t } = useTranslation(['v3/developerSettings']);
  const toast = useToast();

  // Sync pushyManager with atoms
  usePushyManager();

  // Get current state from atoms
  const currentDeviceToken = useAtomValue(pushyDeviceTokenAtom);
  const isInitialized = useAtomValue(pushyInitializedAtom);

  const [deviceToken, setDeviceToken] = useState('');
  const [title, setTitle] = useState('Test Title');
  const [body, setBody] = useState('This is a test push notification from the Tempo dev panel.');
  const [isLoading, setIsLoading] = useState(false);

  const handleSendTestPush = async () => {
    if (!deviceToken.trim()) {
      toast({
        title: 'Device Token is required',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await fetchApi<any>('/pushy/test-push-by-token', { // A new dedicated route for testing
        method: 'POST',
        body: { token: deviceToken, title, body },
      });

      if (result.error) {
        throw new Error(result.error || 'Failed to send push');
      }

      toast({
        title: 'Test Push Sent',
        description: `Successfully sent to token: ${deviceToken.substring(0, 10)}...`,
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top'
      });
    } catch (error) {
      console.error('Failed to send test push:', error);
      toast({
        title: 'Failed to Send Push',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mb-6">
      <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark p-4">
        <h2 className="text-ios-title3 font-semibold mb-3">Pushy Test Panel</h2>
        <p className="text-ios-subhead text-ios-gray dark:text-ios-dark-gray mb-4">
          Send a test notification to a specific device using its Pushy token.
        </p>

        {/* Status Display */}
        <div className="mb-6 p-4 bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md">
          <div className="flex items-center justify-between mb-2">
            <span className="text-ios-body font-medium">Pushy Status:</span>
            <span className={`px-2 py-1 rounded-ios-sm text-ios-caption font-medium ${
              isInitialized
                ? 'bg-ios-green text-white'
                : 'bg-ios-orange text-white'
            }`}>
              {isInitialized ? 'Initialized' : 'Not Initialized'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-ios-body font-medium">Device Token:</span>
            <span className="text-ios-caption text-ios-gray dark:text-ios-dark-gray2">
              {currentDeviceToken ? `${currentDeviceToken.slice(0, 8)}...` : 'None'}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {/* Plugin Availability Check */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                const isAvailable = isPushyPluginAvailable();
                toast({
                  title: 'Pushy Plugin Check',
                  description: isAvailable
                    ? 'Pushy plugin is available and ready to use'
                    : 'Pushy plugin is not available. Make sure you are on a native platform (iOS/Android)',
                  status: isAvailable ? 'success' : 'warning',
                  duration: 5000,
                  isClosable: true,
                  position: 'top'
                });
              }}
              className="px-3 py-2 bg-ios-purple dark:bg-ios-dark-purple text-white rounded-ios-md text-ios-body font-medium hover:bg-purple-700 dark:hover:bg-purple-500 transition-colors"
            >
              Check Plugin
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={deviceToken}
              onChange={(e) => setDeviceToken(e.target.value)}
              placeholder="Enter Pushy device token"
              className="flex-grow px-3 py-2 border border-ios-gray5 dark:border-ios-dark-gray5 rounded-ios-md bg-white dark:bg-ios-dark-gray5 text-ios-body focus:outline-none focus:ring-2 focus:ring-ios-blue dark:focus:ring-ios-dark-blue"
            />
            <button
              onClick={() => {
                try {
                  dualLog.info('🔥 [PushyTestPanel] Use Current button clicked');

                  // Get token from atoms (preferred) or fallback to manager
                  let token = currentDeviceToken || pushyManager.getDeviceToken();

                  dualLog.info('🔥 [PushyTestPanel] Token sources', {
                    currentDeviceToken,
                    managerToken: pushyManager.getDeviceToken(),
                    finalToken: token
                  });

                  if (token) {
                    dualLog.info('🔥 [PushyTestPanel] Token found, validating format', { token });

                    // Validate token format (64-character hex string)
                    if (!/^[a-fA-F0-9]{64}$/.test(token)) {
                      dualLog.warn('🔥 [PushyTestPanel] Token format validation failed', {
                        tokenLength: token.length,
                        isHex: /^[a-fA-F0-9]+$/.test(token)
                      });

                      toast({
                        title: 'Invalid token format',
                        description: 'Invalid token format in storage',
                        status: 'warning',
                        duration: 3000,
                        isClosable: true,
                        position: 'top'
                      });
                      return;
                    }

                    dualLog.info('🔥 [PushyTestPanel] Token validation passed, setting device token');
                    setDeviceToken(token);
                  } else {
                    dualLog.warn('🔥 [PushyTestPanel] No token found');

                    toast({
                      title: 'Token not found',
                      description: 'No Pushy token found. Please ensure the app is registered on a mobile device.',
                      status: 'warning',
                      duration: 3000,
                      isClosable: true,
                      position: 'top'
                    });
                  }
                } catch (error) {
                  dualLog.error('🔥 [PushyTestPanel] Error in Use Current button', { error });

                  toast({
                    title: 'Failed to access token',
                    description: 'Failed to access stored token',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                    position: 'top'
                  });
                }
              }}
              className="px-3 py-2 bg-ios-blue dark:bg-ios-dark-blue text-white rounded-ios-md text-ios-body font-medium hover:bg-blue-700 dark:hover:bg-blue-500 transition-colors"
            >
              Use Current
            </button>
          </div>
          <div>
            <label className="text-ios-headline font-medium mb-2 block">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Notification Title"
              className="w-full px-3 py-2 border border-ios-gray5 dark:border-ios-dark-gray5 rounded-ios-md bg-white dark:bg-ios-dark-gray5 text-ios-body focus:outline-none focus:ring-2 focus:ring-ios-blue dark:focus:ring-ios-dark-blue"
            />
          </div>
          <div>
            <label className="text-ios-headline font-medium mb-2 block">Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Notification Body"
              className="w-full px-3 py-2 border border-ios-gray5 dark:border-ios-dark-gray5 rounded-ios-md bg-white dark:bg-ios-dark-gray5 text-ios-body focus:outline-none focus:ring-2 focus:ring-ios-blue dark:focus:ring-ios-dark-blue"
              rows={3}
            />
          </div>
          <button
            onClick={handleSendTestPush}
            disabled={isLoading}
            className="w-full px-4 py-3 bg-ios-green dark:bg-ios-dark-green text-white rounded-ios-md text-ios-body font-medium hover:bg-green-700 dark:hover:bg-green-500 transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Sending...' : 'Send Test Push'}
          </button>
        </div>
      </div>
    </div>
  );
};